import { useEffect, useMemo, useState } from 'react';

export interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const EMPTY_TIME: TimeLeft = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

const getTimeLeft = (targetDate?: Date | null): TimeLeft => {
  if (!targetDate) return EMPTY_TIME;

  const difference = targetDate.getTime() - Date.now();

  if (difference <= 0) return EMPTY_TIME;

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
};

export const useCountdown = (targetDate?: Date | null) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    getTimeLeft(targetDate),
  );

  useEffect(() => {
    if (!targetDate) {
      setTimeLeft(EMPTY_TIME);
      return;
    }

    const update = () => {
      setTimeLeft(getTimeLeft(targetDate));
    };

    update();

    const timer = setInterval(update, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const paddedTimeLeft = useMemo(
    () => ({
      days: timeLeft.days.toString().padStart(2, '0'),
      hours: timeLeft.hours.toString().padStart(2, '0'),
      minutes: timeLeft.minutes.toString().padStart(2, '0'),
      seconds: timeLeft.seconds.toString().padStart(2, '0'),
    }),
    [timeLeft],
  );

  return {
    timeLeft,
    paddedTimeLeft,
    isExpired:
      !targetDate || Object.values(timeLeft).every((value) => value === 0),
  };
};
